import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from "react";
import { Link, type LinkProps } from "react-router-dom";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  icon?: ReactNode;
  loading?: boolean;
};

type LinkButtonProps = LinkProps & {
  variant?: Variant;
  size?: Size;
  icon?: ReactNode;
};

type AnchorButtonProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  variant?: Variant;
  size?: Size;
  icon?: ReactNode;
};

function classNameFor(variant: Variant, size: Size, className?: string) {
  const variantClass =
    variant === "primary"
      ? "btn-primary"
      : variant === "secondary"
        ? "btn-secondary"
        : variant === "danger"
          ? "btn-danger"
          : "btn-ghost";
  const sizeClass = size === "sm" ? "btn-sm" : "";
  return ["btn", variantClass, sizeClass, className].filter(Boolean).join(" ");
}

export function Button({
  children,
  variant = "primary",
  size = "md",
  icon,
  loading = false,
  className,
  type = "button",
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button className={classNameFor(variant, size, className)} type={type} disabled={disabled || loading} {...props}>
      {loading ? <span className="btn-spinner" aria-hidden="true" /> : icon ? <span className="button-icon" data-icon="inline-start" aria-hidden="true">{icon}</span> : null}
      <span>{children}</span>
    </button>
  );
}

export function LinkButton({
  children,
  variant = "primary",
  size = "md",
  icon,
  className,
  ...props
}: LinkButtonProps) {
  return (
    <Link className={classNameFor(variant, size, className)} {...props}>
      {icon ? <span className="button-icon" data-icon="inline-start" aria-hidden="true">{icon}</span> : null}
      <span>{children}</span>
    </Link>
  );
}

export function AnchorButton({
  children,
  variant = "primary",
  size = "md",
  icon,
  className,
  ...props
}: AnchorButtonProps) {
  return (
    <a className={classNameFor(variant, size, className)} {...props}>
      {icon ? <span className="button-icon" data-icon="inline-start" aria-hidden="true">{icon}</span> : null}
      <span>{children}</span>
    </a>
  );
}
